import React from 'react';
import { formatCurrency } from '../../../utils/assessmentResults/formatters';

/**
 * Implementation accordion item component
 */
export const ImplementationAccordion = ({ recc, implementationData, isExpanded, onToggle }) => {
  const costCategoryClass = recc.cost_category === 'low_cost'
    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
    : recc.cost_category === 'medium_cost'
      ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
      : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* RECC Header */}
      <div className="bg-gray-50 dark:bg-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <span className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs px-2 py-1 rounded font-medium">
              ARC {recc.arc_code}
            </span>
            <h4 className="font-semibold text-gray-900 dark:text-white">
              {recc.title || implementationData.title}
            </h4>
            <span className={`text-xs px-2 py-1 rounded font-medium ${costCategoryClass}`}>
              {recc.cost_category?.replace('_', '-') || 'Low-cost'}
            </span>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                {formatCurrency(recc.annual_savings || 15000)}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Annual Savings</div>
            </div>
            <div className="text-right">
              <div className="text-sm font-medium text-blue-600 dark:text-blue-400">
                {(recc.payback_years || 1.2).toFixed(1)} years
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Payback</div>
            </div>
            <button
              onClick={onToggle}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1 rounded text-xs font-medium transition-colors"
            >
              {isExpanded ? 'Hide Details' : 'View Details'}
            </button>
          </div>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          {recc.description || implementationData.description}
        </p>
      </div>

      {/* Expanded Implementation Guide */}
      {isExpanded && (
        <div className="p-6 bg-white dark:bg-gray-800">
          <div className="space-y-6">
            {/* Key Implementation Levers */}
            <div>
              <h5 className="font-semibold text-gray-800 dark:text-gray-200 mb-4 border-b border-gray-300 dark:border-gray-700 pb-2">
                Key Implementation Levers
              </h5>
              <div className="grid gap-4">
                {implementationData.keyLevers.map((lever, leverIndex) => (
                  <div key={leverIndex} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h6 className="font-medium text-emerald-700 dark:text-emerald-400 mb-3">
                      {lever.category}
                    </h6>
                    <ul className="space-y-2">
                      {lever.actions.map((action, actionIndex) => (
                        <li key={actionIndex} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <span className="text-emerald-500 dark:text-emerald-400 mt-0.5 flex-shrink-0">•</span>
                          {action}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Cost Categories */}
            <div>
              <h5 className="font-semibold text-gray-800 dark:text-gray-200 mb-4 border-b border-gray-300 dark:border-gray-700 pb-2">
                Implementation by Cost & Payback
              </h5>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-4">
                  <h6 className="font-medium text-green-800 dark:text-green-400 mb-3">
                    Low-Cost (weeks to &lt;1 year)
                  </h6>
                  <ul className="space-y-2">
                    {implementationData.costCategories.lowCost.map((item, i) => (
                      <li key={i} className="text-sm text-green-700 dark:text-green-400">
                        • {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/30 rounded-lg p-4">
                  <h6 className="font-medium text-yellow-800 dark:text-yellow-400 mb-3">
                    Medium-Cost (1–3 years)
                  </h6>
                  <ul className="space-y-2">
                    {implementationData.costCategories.mediumCost.map((item, i) => (
                      <li key={i} className="text-sm text-yellow-700 dark:text-yellow-400">
                        • {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-4">
                  <h6 className="font-medium text-red-800 dark:text-red-400 mb-3">
                    High-Cost (3–5 years)
                  </h6>
                  <ul className="space-y-2">
                    {implementationData.costCategories.highCost.map((item, i) => (
                      <li key={i} className="text-sm text-red-700 dark:text-red-400">
                        • {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Risk Factors */}
            <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-4">
              <h5 className="font-semibold text-red-800 dark:text-red-400 mb-3">
                Risk Factors & Mitigation
              </h5>
              <ul className="space-y-2">
                {implementationData.riskFactors.map((risk, riskIndex) => (
                  <li key={riskIndex} className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400">
                    <span className="text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0">⚠</span>
                    {risk}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

